<template>
  <div class="content-wrapper">
    <section class="content-header nt-page-header">
      <div>
        <h1>Notifications</h1>
        <p class="nt-page-sub">Template status changes from WhatsApp and what they meant for your campaigns.</p>
      </div>
      <button type="button" class="btn btn-default btn-sm" :disabled="!center.unread" @click="readAll">
        <i class="fa fa-check"></i> Mark all read
      </button>
    </section>

    <section class="content">
      <div class="nt-filters">
        <div class="btn-group">
          <button v-for="f in views" :key="f.key" type="button" class="btn btn-sm" :class="view === f.key ? 'btn-primary' : 'btn-default'" @click="setView(f.key)">
            {{ f.label }}<span v-if="f.key === 'unread' && center.unread"> ({{ center.unread }})</span>
          </button>
        </div>
        <nv-select v-model="severity" class="form-control input-sm nt-select" @change="reload">
          <option value="">All severities</option>
          <option value="critical">Critical</option>
          <option value="warning">Warning</option>
          <option value="success">Success</option>
          <option value="info">Info</option>
        </nv-select>
        <nv-select v-model="type" class="form-control input-sm nt-select" @change="reload">
          <option value="">Templates and campaigns</option>
          <option value="template_">Templates</option>
          <option value="campaign_">Campaigns</option>
        </nv-select>
      </div>

      <div class="nt-list">
        <div v-if="!items.length && !loading" class="nt-empty">
          <i class="fa fa-bell-slash-o"></i>
          <p>{{ view === 'unread' ? "You're all caught up." : 'No notifications match these filters.' }}</p>
        </div>
        <div v-for="n in items" :key="n.id" class="nt-row" :class="{ 'nt-unread': !n.is_read }">
          <i class="fa nt-row-icon" :class="[icon(n), `nt-sev-${n.severity}`]"></i>
          <div class="nt-row-main">
            <div class="nt-row-top">
              <strong>{{ n.title }}</strong>
              <span class="nt-chip" :class="`nt-chip-${n.severity}`">{{ n.severity }}</span>
            </div>
            <p v-if="n.body" class="nt-row-body">{{ n.body }}</p>
            <div v-for="(p, i) in proposalsOf(n)" :key="i" class="nt-proposal">
              <div class="nt-proposal-head">Proposed change · {{ p.name }} ({{ p.language }})</div>
              <ul v-if="p.problems && p.problems.length" class="nt-proposal-problems">
                <li v-for="(problem, j) in p.problems" :key="j">{{ problem }}</li>
              </ul>
              <p v-if="p.why" class="nt-proposal-why">{{ p.why }}</p>
              <div v-if="p.proposed" class="nt-diff">
                <div class="nt-diff-col nt-diff-old"><span>Now</span><pre>{{ p.current }}</pre></div>
                <div class="nt-diff-col nt-diff-new">
                  <span>Proposed <button type="button" class="btn btn-xs btn-default nt-copy" @click="copy(p.proposed)">{{ copied === p.proposed ? 'Copied' : 'Copy' }}</button></span>
                  <pre>{{ p.proposed }}</pre>
                </div>
              </div>
              <p v-else-if="!p.problems || !p.problems.length" class="nt-proposal-why">No change could be suggested automatically. Review the template against WhatsApp's rules.</p>

              <p v-if="resubmittedOf(n, p)" class="nt-resubmitted">
                <i class="fa fa-check-circle"></i> Resubmitted as <strong>{{ resubmittedOf(n, p).newName }}</strong> by {{ resubmittedOf(n, p).by }}. Campaigns using it can be resumed once WhatsApp approves it.
              </p>
              <template v-else-if="canResubmit(n)">
                <button v-if="!editorFor(n, p)" type="button" class="btn btn-xs btn-success nt-accept" @click="openEditor(n, p)">Accept and resubmit…</button>
                <div v-else class="nt-editor">
                  <label>New body (sent to WhatsApp as a new version; the campaigns follow it)</label>
                  <textarea v-model="editorFor(n, p).body" class="form-control" rows="4" @input="syncSamples(n, p)"></textarea>
                  <div v-for="(sample, k) in editorFor(n, p).samples" :key="k" class="nt-sample">
                    <label v-text="sampleLabel(k)"></label>
                    <input v-model="editorFor(n, p).samples[k]" class="form-control input-sm" placeholder="A realistic example, in the template's language">
                  </div>
                  <p v-if="editorFor(n, p).error" class="nt-editor-error">{{ editorFor(n, p).error }}</p>
                  <div class="nt-editor-actions">
                    <button type="button" class="btn btn-sm btn-success" :disabled="editorFor(n, p).busy" @click="resubmit(n, p)">
                      <i v-if="editorFor(n, p).busy" class="fa fa-spinner fa-spin"></i> Resubmit to WhatsApp
                    </button>
                    <button type="button" class="btn btn-sm btn-default" :disabled="editorFor(n, p).busy" @click="closeEditor(n, p)">Cancel</button>
                  </div>
                </div>
              </template>
            </div>
            <a v-if="n.details && n.details.appealUrl" :href="n.details.appealUrl" target="_blank" rel="noopener" class="nt-appeal">
              <i class="fa fa-external-link"></i> Request a review or appeal in WhatsApp Manager
            </a>
            <div class="nt-row-meta">
              <span>{{ ago(n.created_at) }}</span>
              <span v-if="countriesOf(n)">· {{ countriesOf(n) }}</span>
            </div>
          </div>
          <div class="nt-row-actions">
            <button v-if="n.link" type="button" class="btn btn-sm btn-primary" @click="openItem(n)">Open</button>
            <button v-if="!n.is_read" type="button" class="btn btn-sm btn-default" @click="markRead(n)">Mark read</button>
          </div>
        </div>
        <div v-if="loading" class="nt-loading"><i class="fa fa-spinner fa-spin"></i> Loading…</div>
        <button v-if="hasMore && !loading" type="button" class="btn btn-default btn-sm nt-more" @click="loadMore">Load older</button>
      </div>
    </section>
  </div>
</template>

<script>
import api from '../../../utils/api'
import { notificationCenter, markNotificationRead, markAllNotificationsRead, SEVERITY_ICONS, timeAgo } from '../../../utils/notifications'

const PAGE_SIZE = 25

export default {
  name: 'notifications-page',
  data () {
    return {
      center: notificationCenter.$data,
      views: [{ key: 'all', label: 'All' }, { key: 'unread', label: 'Unread' }],
      view: 'all',
      severity: '',
      type: '',
      items: [],
      loading: false,
      hasMore: false,
      copied: null,
      // "<notification id>|<template>|<language>" -> { body, samples, busy, error }
      editors: {}
    }
  },
  created () {
    this.reload()
    notificationCenter.$on('notification', this.onPushed)
  },
  beforeDestroy () {
    notificationCenter.$off('notification', this.onPushed)
  },
  methods: {
    query (beforeId) {
      const params = { limit: PAGE_SIZE }
      if (beforeId) params.beforeId = beforeId
      if (this.view === 'unread') params.unreadOnly = 'true'
      if (this.severity) params.severity = this.severity
      if (this.type) params.type = this.type
      return params
    },
    async fetchPage (beforeId) {
      this.loading = true
      try {
        const { data, status } = await api.buildRequest('notifications', 'get', this.query(beforeId))
        if (status !== 200) throw new Error((data && data.payload && data.payload.message) || 'Request failed')
        const page = data.payload.items
        this.items = beforeId ? this.items.concat(page) : page
        this.hasMore = page.length === PAGE_SIZE
        notificationCenter.unread = data.payload.unread
      } catch (err) {
        this.$swal({ type: 'error', text: `Couldn't load notifications: ${err.message}` })
      } finally {
        this.loading = false
      }
    },
    reload () {
      this.fetchPage(null)
    },
    loadMore () {
      const last = this.items[this.items.length - 1]
      if (last) this.fetchPage(last.id)
    },
    setView (key) {
      this.view = key
      this.reload()
    },
    onPushed (n) {
      if (this.severity && n.severity !== this.severity) return
      if (this.type && !n.type.startsWith(this.type)) return
      if (!this.items.some(i => i.id === n.id)) this.items.unshift(Object.assign({}, n))
    },
    // The page keeps its own list; keep it and the bell's in step.
    syncRead (n) {
      n.is_read = true
      const inBell = notificationCenter.items.find(i => i.id === n.id)
      if (inBell) inBell.is_read = true
    },
    markRead (n) {
      const target = notificationCenter.items.find(i => i.id === n.id) || n
      markNotificationRead(target)
      this.syncRead(n)
    },
    openItem (n) {
      this.markRead(n)
      this.$router.push(n.link)
    },
    async readAll () {
      await markAllNotificationsRead()
      this.items.forEach(n => { n.is_read = true })
      if (this.view === 'unread') this.reload()
    },
    icon (n) {
      return SEVERITY_ICONS[n.severity] || SEVERITY_ICONS.info
    },
    ago (value) {
      return timeAgo(value)
    },
    // Suggested edits for a rejected or paused template (server/src/notifications/templateFix.js).
    proposalsOf (n) {
      return n.details && Array.isArray(n.details.proposedChanges) ? n.details.proposedChanges : []
    },
    canResubmit (n) {
      return ['template_rejected', 'template_paused'].includes(n.type)
    },
    resubmittedOf (n, p) {
      const list = (n.details && n.details.resubmitted) || []
      return list.find(r => r.templateName === p.name && r.language === p.language) || null
    },
    editorKey (n, p) {
      return `${n.id}|${p.name}|${p.language}`
    },
    editorFor (n, p) {
      return this.editors[this.editorKey(n, p)] || null
    },
    openEditor (n, p) {
      const body = p.proposed || p.current || ''
      const count = (body.match(/\{\{\s*\d+\s*\}\}/g) || []).length
      this.$set(this.editors, this.editorKey(n, p), { body, samples: Array(count).fill(''), busy: false, error: '' })
    },
    // One sample input per {{n}} in the body as it's edited.
    syncSamples (n, p) {
      const editor = this.editorFor(n, p)
      const count = (editor.body.match(/\{\{\s*\d+\s*\}\}/g) || []).length
      if (editor.samples.length !== count) editor.samples = Array(count).fill('').map((x, i) => editor.samples[i] || '')
    },
    sampleLabel (k) {
      return `Sample for {{${k + 1}}}`
    },
    closeEditor (n, p) {
      this.$delete(this.editors, this.editorKey(n, p))
    },
    async resubmit (n, p) {
      const editor = this.editorFor(n, p)
      this.syncSamples(n, p)
      editor.busy = true
      editor.error = ''
      try {
        const { data, status } = await api.buildRequest(`notifications/${n.id}/resubmit`, 'post', {
          templateName: p.name,
          language: p.language,
          body: editor.body,
          samples: editor.samples
        })
        if (status !== 200) throw new Error((data && data.payload && data.payload.message) || 'Request failed')
        const details = Object.assign({}, n.details || {})
        details.resubmitted = (details.resubmitted || []).concat(data.payload)
        n.details = details
        this.closeEditor(n, p)
        this.$swal({ type: 'success', text: `Sent to WhatsApp as "${data.payload.newName}".` })
      } catch (err) {
        editor.error = err.message
      } finally {
        if (this.editorFor(n, p)) editor.busy = false
      }
    },
    copy (text) {
      if (!navigator.clipboard) return
      navigator.clipboard.writeText(text).then(() => { this.copied = text })
    },
    countriesOf (n) {
      return n.details && Array.isArray(n.details.countries) ? n.details.countries.join(', ') : ''
    }
  }
}
</script>

<style scoped>
.nt-page-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; }
.nt-page-header h1 { margin: 0; }
.nt-page-sub { margin: 4px 0 0; color: #667085; font-size: 13px; }
.nt-filters { display: flex; flex-wrap: wrap; gap: 10px; align-items: center; margin-bottom: 12px; }
.nt-select { width: auto; min-width: 170px; }
.nt-list { background: #fff; border: 1px solid #eaecf0; border-radius: 10px; overflow: hidden; }
.nt-row { display: flex; gap: 12px; padding: 14px 16px; border-bottom: 1px solid #f2f4f7; text-align: left; }
.nt-row:last-of-type { border-bottom: 0; }
.nt-unread { background: #f0fdfa; }
.nt-row-icon { font-size: 18px; margin-top: 2px; }
.nt-row-main { flex: 1; min-width: 0; }
.nt-row-top { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.nt-row-top strong { color: #101828; }
.nt-row-body { margin: 4px 0; color: #475467; font-size: 13px; white-space: pre-line; }
.nt-row-meta { font-size: 12px; color: #98a2b3; display: flex; gap: 6px; }
.nt-row-actions { display: flex; flex-direction: column; gap: 6px; flex-shrink: 0; min-width: 96px; }
.nt-chip { font-size: 11px; padding: 1px 8px; border-radius: 10px; text-transform: capitalize; }
.nt-chip-critical { background: #fef3f2; color: #b42318; }
.nt-chip-warning { background: #fffaeb; color: #b54708; }
.nt-chip-success { background: #ecfdf3; color: #027a48; }
.nt-chip-info { background: #f0fdfa; color: #0e7c72; }
.nt-sev-critical { color: #d92d20; }
.nt-sev-warning { color: #f79009; }
.nt-sev-success { color: #12b76a; }
.nt-sev-info { color: #0e9488; }
.nt-proposal { margin: 8px 0; padding: 10px 12px; border: 1px solid #eaecf0; border-radius: 8px; background: #fcfcfd; }
.nt-proposal-head { font-size: 12px; font-weight: 600; color: #344054; margin-bottom: 4px; }
.nt-proposal-problems { margin: 4px 0; padding-left: 18px; font-size: 12.5px; color: #b42318; }
.nt-proposal-why { margin: 4px 0; font-size: 12.5px; color: #475467; }
.nt-diff { display: flex; gap: 10px; margin-top: 6px; }
.nt-diff-col { flex: 1; min-width: 0; }
.nt-diff-col > span { display: flex; align-items: center; gap: 6px; min-height: 24px; font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em; color: #667085; margin-bottom: 3px; }
.nt-diff-col pre { margin: 0; padding: 8px 10px; font-family: inherit; font-size: 12.5px; white-space: pre-wrap; word-break: break-word; border-radius: 6px; border: 0; }
.nt-diff-old pre { background: #fef3f2; color: #7a271a; }
.nt-diff-new pre { background: #ecfdf3; color: #054f31; }
.nt-copy { text-transform: none; letter-spacing: 0; }
.nt-accept { margin-top: 8px; }
.nt-appeal { display: inline-block; margin: 6px 0 2px; font-size: 12.5px; color: #0e7c72; }
.nt-resubmitted { margin: 8px 0 0; font-size: 12.5px; color: #027a48; }
.nt-editor { margin-top: 8px; display: flex; flex-direction: column; gap: 6px; }
.nt-editor label { font-size: 12px; font-weight: 600; color: #344054; margin: 0; }
.nt-editor textarea { font-size: 13px; }
.nt-sample { display: flex; flex-direction: column; gap: 3px; }
.nt-editor-error { margin: 0; font-size: 12.5px; color: #b42318; }
.nt-editor-actions { display: flex; gap: 8px; }
.nt-empty { padding: 40px 16px; text-align: center; color: #667085; }
.nt-empty .fa { font-size: 28px; color: #98a2b3; }
.nt-loading { padding: 14px; text-align: center; color: #667085; }
.nt-more { margin: 12px auto; display: block; }
@media (max-width: 767px) {
  .nt-diff { flex-direction: column; }
  .nt-row { flex-wrap: wrap; }
  .nt-row-actions { flex-basis: 100%; flex-direction: row; align-items: flex-start; padding-left: 30px; }
}
</style>



// WEBPACK FOOTER //
// NotificationsPage.vue?d70592bc